<template>
  <component
    :is="as"
    :type="type"
    :class="classes"
    v-bind="$attrs"
    role="button"
    v-on="$listeners"
  >
    <EIcon
      :icon="icon"
      :class="[`icon-button__icon-${icon}`, iconClass]"
      :size="iconSize"
      class="icon-button__icon"
      data-test-id="icon-button-icon"
    />
  </component>
</template>

<script>
// NOTE: ESLint on CI having problems finding the following imports
// Therefore we disabled the rule temporaly and will fix it in a separate PR
/* eslint-disable */
import classes from '~~/common/vue/mixins/classes';
import EIcon from '~~/common/vue/components/icon';

/* eslint-enable */
export const elevations = ['0', '1', '2'];
export const sizes = ['xs', 's', 'm', 'l'];
export const tags = ['button', 'a', 'nuxt-link'];
export const variants = ['bare', 'outline-dark', 'solid-light', 'solid-dark'];

export default {
  name: 'EIconButton',
  components: {
    EIcon,
  },
  mixins: [classes],
  props: {
    /**
     * `button, a`
     */
    as: {
      type: String,
      default: 'button',
      validator: (value) => tags.includes(value),
    },
    disabled: {
      type: Boolean,
      default: false,
      cssClass: true,
    },
    /**
     * `0, 1, 2`
     */
    elevation: {
      type: String,
      default: '0',
      validator: (value) => elevations.includes(value),
      cssClass: true,
    },
    /**
     * Passed to Icon component as `icon` prop
     */
    icon: {
      type: String,
      required: true,
    },
    /**
     * Passed to Icon component as class
     */
    iconClass: {
      type: [String, Array, Object],
      default: '',
    },
    /**
     * Passed to Icon component as `size` prop
     */
    iconSize: {
      type: String,
      default: undefined,
    },
    rounded: {
      type: Boolean,
      default: false,
      cssClass: true,
    },
    /**
     * `xs, s, m, l`
     */
    size: {
      type: String,
      default: 'm',
      validator: (value) => sizes.includes(value),
      cssClass: true,
    },
    /**
     * `bare, outline-dark, solid-light, solid-dark`
     */
    variant: {
      type: String,
      default: 'bare',
      validator: (value) => variants.includes(value),
      cssClass: true,
    },
  },
  computed: {
    type() {
      return this.as === 'button' ? 'button' : undefined;
    },
  },
};
</script>

<style lang="scss" scoped>
$variants: (
  bare: (
    color: $gray-40,
    active-color: $gray-60,
  ),
  outline-dark: (
    color: $gray-40,
    border-color: $gray-20,
    active-color: $gray-70,
    active-background-color: $gray-10,
    active-border-color: $gray-10,
  ),
  solid-light: (
    color: $gray-40,
    background-color: $white,
    active-color: $gray-60,
    active-background-color: $gray-10,
  ),
  solid-dark: (
    color: $gray-20,
    background-color: $gray-60,
    active-color: $white,
    active-background-color: $gray-50,
  ),
);

$sizes: (
  xs: (
    font: $font-1s,
    // non-standard spacing - make button height exactly matching icon-m height
    height: $icon-size-m,
    padding: 0,
  ),
  s: (
    font: $font-1s,
    size: $space-2l,
  ),
  m: (
    font: $font-m,
    size: $space-3l,
  ),
  l: (
    font: $font-l,
    size: $space-4l,
  ),
);

.icon-button {
  -webkit-tap-highlight-color: $transparent;
  position: relative;
  display: inline-flex;
  background-color: $transparent;
  font-family: $family;
  white-space: nowrap;
  text-align: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  border-radius: $border-radius-s;
  outline: 0;
  padding: 0;
  appearance: none;
  align-items: center;
  letter-spacing: 0.1px;
  transition: all $timing-2s $easing;

  &,
  &:hover,
  &:focus {
    text-decoration: none;
  }

  &:focus {
    outline: none;
  }

  @each $name, $elevation in $elevations {
    &--elevation-#{$name} {
      @include elevation($name);
    }
  }

  @each $name, $map in $variants {
    $color: map-get($map, color);
    $background-color: map-get($map, background-color);
    $border-color: map-get($map, border-color);
    $active-color: map-get($map, active-color);
    $active-background-color: map-get($map, active-background-color);
    $active-border-color: map-get($map, active-border-color);

    &--variant-#{$name} {
      color: map-get($map, color);

      @if $background-color {
        background-color: $background-color;
      }

      @if $border-color {
        border: $border-width solid $border-color;
      }

      &:active,
      &:hover,
      &:focus {
        color: $active-color;

        @if $active-background-color {
          background-color: $active-background-color;
        }

        @if $active-border-color {
          border-color: $active-border-color;
        }
      }
    }
  }

  @each $name, $map in $sizes {
    $font: map-get($map, font);
    $size: map-get($map, size);

    &--size-#{$name} {
      font-size: $font;
      width: $size;
      height: $size;
    }
  }

  &--disabled {
    opacity: $opacity-m;
    pointer-events: none;
  }

  &--rounded {
    border-radius: $border-radius-full;
  }

  &__icon {
    display: inline-flex;
  }
}
</style>

<docs>
A Button with a single icon.

### Examples

Icons:

```js
<div>
  <EIconButton icon="chevron-left" />
  <EIconButton icon="source-google-maps" />
</div>
```

Icon classes:

```js
<div>
  <EIconButton icon="chevron-left" icon-class="my-chevron" />
</div>
```

Icon sizes:

```js
<div>
  <EIconButton icon="close" icon-size="s" />
  <EIconButton icon="chevron-left" icon-size="m" />
</div>
```

Variants:

```js
<div>
  <div style="padding: 10px;">
    <EIconButton icon="chevron-left" variant="bare" />
    <EIconButton icon="chevron-left" variant="outline-dark" />
    <EIconButton icon="chevron-left" variant="solid-light" />
    <EIconButton icon="chevron-left" variant="solid-dark" />
  </div>
  <div style="background-color: #000000; padding: 10px;">
    <EIconButton icon="chevron-left" variant="bare" />
    <EIconButton icon="chevron-left" variant="outline-dark" />
    <EIconButton icon="chevron-left" variant="solid-light" />
    <EIconButton icon="chevron-left" variant="solid-dark" />
  </div>
</div>
```

Elevations (provisional):

```js
<div>
  <EIconButton icon="chevron-left" elevation="1" />
  <EIconButton icon="chevron-left" elevation="2" />
</div>
```

Sizes:

```js
<div>
  <EIconButton icon="chevron-left" size="s" elevation="1" />
  <EIconButton icon="chevron-left" size="m" elevation="1" />
</div>
```

Rounded + elevation:

```js
<div>
  <EIconButton icon="chevron-left" variant="solid-light" rounded />
  <EIconButton icon="chevron-left" variant="solid-light" rounded elevation="1" />
  <EIconButton icon="chevron-left" variant="solid-light" rounded elevation="2" />
  <EIconButton icon="chevron-left" variant="solid-dark" rounded />
  <EIconButton icon="chevron-left" variant="solid-dark" rounded elevation="1" />
  <EIconButton icon="chevron-left" variant="solid-dark" rounded elevation="2" />
</div>
```
</docs>
